import React from "react";
import {
  Page,
  Layout,
} from '@shopify/polaris';
import "../../styles/privacy.scss";
import PrivacyPolicyRequest from "../../requests/PrivacyPolicyRequest";
import ChoiceHelper from "../../helpers/ChoiceHelper";
import TableHelper from "../../helpers/TableHelper";
import { CsValidationForm } from "../../shared/csValidationForm";
import LegalAppBridge from "../../LegalAppBridge";
import PrivacyCollectInfo from "./PrivacyCollectInfo";
import PrivacyPartners from "./PrivacyPartners";
import PrivacyMarketing from "./PrivacyMarketing";
import PrivacyCookies from "./PrivacyCookies";
import PrivacySkeleton from "./PrivacySkeleton";
import CSPageActions from "../CSPageActions";
import Env from "../../Env";

export default class PrivacyPolicy extends React.Component {
  validatorClassInfo = 'information';
  validatorClassPartners = 'partners';
  validatorClassAnalyze = 'analyze';
  validatorClassCookies = 'cookies';
  savedState = {};
  untrackState = ['init', 'saving', 'validateError', 'partnerTableDefault', 'uploadToken'];

  partnerTableItemSkeleton = { name: '', 'function': '', country: '', purposes: '', privacy_policies: '' };
  cookieTableItemSkeleton = { name: '', link: '', purposes: '', readonly: false };

  state = {
    init: true,
    saving: false,
    validateError: false,
    uploadToken: '',

    informationYouCollect: [],
    concernEmail: '',
    receiveInfoFromPartner: false,
    partnerTable: [this.partnerTableItemSkeleton],
    partnerTableDefault: [],
    analyticActive: false,
    analytics: [],
    emailMarketingActive: false,
    emailMarketing: [],
    remarketingActive: false,
    remarketing: [],
    cookieActive: false,
    cookieTable: [this.cookieTableItemSkeleton],
    cookieCustomization: {},
    cookieIconFile: null,
  }

  discardChanges = () => {
    this.setState(this.props.getSavedState(this.savedState, this.untrackState));
  }

  componentDidMount() {
    this.handleLoad();
    this.loadUploadToken();
  }

  handleLoad = () => {
    PrivacyPolicyRequest.loadPrivacySettings(
      (response) => {
        if (response.success) {
          this.setState(response.data);
          this.savedState = this.props.getStateToBeSaved(this.state, this.untrackState);
        } else {
          LegalAppBridge.toastError(response.reason);
        }
      },
      () => {
        LegalAppBridge.toastError('Unknown error');
      },
      () => {
        this.setState({ init: false });
      }
    );
  }

  loadUploadToken = () => {
    PrivacyPolicyRequest.fetchUploadToken().then(response => {
      if (response.token) {
        this.setState({ uploadToken: response.token });
      }
    });
  }

  handleSave = () => {
    const state = this.state;
    let validation = true,
      validators = [this.validatorClassInfo, this.validatorClassAnalyze];

    state.receiveInfoFromPartner && validators.push(this.validatorClassPartners);
    state.cookieActive && validators.push(this.validatorClassCookies);
    validators.map(validatorClass => validation = validation && CsValidationForm.validate(validatorClass));

    if (!validation) {
      this.setState({ validateError: true });
    } else {
      this.setState({ saving: true, validateError: false });

      // Save file first to achieve URL
      this._saveFile()
        .then(() => {
          // Then, save configuration
          let privacyPolicies = { ...this.state };
          [...this.untrackState, 'cookieIconFile'].map(key => delete privacyPolicies[key]);

          return PrivacyPolicyRequest.savePrivacySettings(privacyPolicies);
        })
        .then(response => {
          if (response.success) {
            LegalAppBridge.toast('Settings saved successfully');
            this.savedState = this.props.getStateToBeSaved(this.state, this.untrackState);
          } else {
            LegalAppBridge.toastError(response.reason);
          }
        })
        .catch(error => LegalAppBridge.toastError(error ? error : 'Unknown error'))
        .finally(() => this.setState({ saving: false }));
    }
  }

  /**
   * @returns {Promise<never>|Promise<void>}
   * @private
   */
  _saveFile = () => {
    if (this.state.cookieIconFile && this.state.uploadToken) {
      return PrivacyPolicyRequest.uploadCookieIcon('cookieIconFile', this.state.cookieIconFile, {
        csStore: Env.getStoreName(),
        csToken: this.state.uploadToken,
      })
        .then(response => {
          if (!response.url) {
            // Reject with error
            return Promise.reject(response.error.message);
          }

          // Take icon URL to state
          this.handleChangeObjectState('cookieCustomization', { icon: response.url });
        });
    }

    // Resolve in all other cases
    return Promise.resolve();
  }

  handleArrayChange = (name, index, field) => event => {
    const changedValue = event.target ? event.target.value : event;
    this.setState({ [name]: TableHelper.onChange(changedValue, this.state[name], index, field) });
  }

  handleArrayAdded = name => () => {
    this.setState({ [name]: [...this.state[name], this[name + 'ItemSkeleton']] });
  }

  handleArrayRemoved = (name, removeIndex) => () => {
    this.setState({ [name]: TableHelper.removeRow(this.state[name], removeIndex) });
  }

  handleChange = name => event => {
    this.setState({ [name]: event.target ? event.target.value : event });
  }

  handleChangeRaw = (name, value) => {
    this.setState({ [name]: value });
  }

  handleChangeTextFieldOfChoice = (masterListKey, subListKeys, itemKey) => event => {
    const
      changedValue = event.target ? event.target.value : event,
      targetState = this.state[masterListKey],
      stateChanged = this._changeChoiceListTextField(targetState, subListKeys, itemKey, changedValue);
    this.setState({ [masterListKey]: stateChanged });
  }

  _changeChoiceListTextField(masterList, subListKeys, itemKey, changedValue) {
    if (!subListKeys.length) {
      // Change for master list
      return masterList.map(item => {
        let itemModify = Object.assign({}, item);
        if (itemModify.key === itemKey && typeof itemModify.text === 'string') {
          itemModify.text = changedValue;
        }
        return itemModify;
      });
    } else {
      // Change for sublist
      const firstSublistKey = subListKeys.shift();
      return masterList.map(item => {
        let itemModify = Object.assign({}, item);
        if (itemModify.key === firstSublistKey) {
          itemModify.options = this._changeChoiceListTextField(itemModify.options, subListKeys, itemKey, changedValue);
        }
        return itemModify;
      });
    }
  }

  handleChoiceListChange = (masterListKey, subListKeys) => event => {
    const
      changedValue = event.target ? event.target.value : event,
      targetState = this.state[masterListKey],
      stateChanged = this._changeChoiceList(targetState, subListKeys, changedValue);
    this.setState({ [masterListKey]: stateChanged });
  }

  _changeChoiceList(masterList, subListKeys, changedValue) {
    if (!subListKeys.length) {
      // Change for master list
      return ChoiceHelper.onChange(changedValue, masterList);
    } else {
      // Change for sublist
      const firstSublistKey = subListKeys.shift();
      return masterList.map(item => {
        let itemModify = Object.assign({}, item);
        if (itemModify.key === firstSublistKey) {
          itemModify.options = this._changeChoiceList(itemModify.options, subListKeys, changedValue);
        }
        return itemModify;
      });
    }
  }

  handleTogglePartner = (e) => {
    const dataset = e.target.closest('p.partner-tag').dataset,
      partnerIndex = dataset.index,
      partnerName = dataset.name,
      enable = dataset.include;
    if (typeof partnerIndex !== 'undefined' && typeof partnerName !== 'undefined' && typeof enable !== 'undefined') {
      if (enable === 'true') {
        this.setState({ partnerTable: this.state.partnerTable.filter(item => item.name !== partnerName) });
      } else {
        const addedPartner = this.state.partnerTableDefault[partnerIndex];
        if (addedPartner) {
          this.setState({ partnerTable: [...this.state.partnerTable, addedPartner] });
        }
      }
    }
  }

  handleChangeObjectState = (name, overrideObject) => {
    this.setState((state) => {
      const updatedValue = Object.assign({}, state[name], overrideObject);
      return { [name]: updatedValue };
    });
  }

  render() {
    const state = this.state;

    if (state.init) {
      return <PrivacySkeleton />
    }

    return (
      <Page fullwidth>
        <Layout>
          <PrivacyCollectInfo
            validatorClass={this.validatorClassInfo}
            informationYouCollect={state.informationYouCollect} concernEmail={state.concernEmail}
            handleChange={this.handleChange}
            handleChoiceListChange={this.handleChoiceListChange}
            handleChangeTextFieldOfChoice={this.handleChangeTextFieldOfChoice}
          />

          <PrivacyPartners
            validatorClass={this.validatorClassPartners}
            receiveInfoFromPartner={state.receiveInfoFromPartner}
            partnerTableDefault={state.partnerTableDefault} partnerTable={state.partnerTable}
            handleChange={this.handleChange} handleArrayChange={this.handleArrayChange}
            handleArrayAdded={this.handleArrayAdded} handleArrayRemoved={this.handleArrayRemoved}
            handleTogglePartner={this.handleTogglePartner}
          />

          <PrivacyMarketing
            validatorClass={this.validatorClassAnalyze}
            analyticActive={state.analyticActive} analytics={state.analytics}
            emailMarketingActive={state.emailMarketingActive} emailMarketing={state.emailMarketing}
            remarketingActive={state.remarketingActive} remarketing={state.remarketing}
            handleChange={this.handleChange}
            handleChoiceListChange={this.handleChoiceListChange}
            handleChangeTextFieldOfChoice={this.handleChangeTextFieldOfChoice}
          />

          <PrivacyCookies
            validatorClass={this.validatorClassCookies}
            cookieActive={state.cookieActive}
            cookieTable={state.cookieTable}
            cookieIconFile={state.cookieIconFile}
            cookieCustomization={state.cookieCustomization} handleChangeObjectState={this.handleChangeObjectState}
            handleChangeRaw={this.handleChangeRaw} handleArrayChange={this.handleArrayChange}
            handleArrayAdded={this.handleArrayAdded} handleArrayRemoved={this.handleArrayRemoved}
          />
        </Layout>

        <CSPageActions
          validateError={state.validateError} saving={state.saving}
          showSaveBar={Object.keys(this.savedState).length && !this.props.shallowCompareEqual(this.savedState, state, this.untrackState)}
          handleSave={this.handleSave} discardChanges={this.discardChanges} handleLoad={this.handleLoad}
        />
      </Page>
    )
  }
}
